import { type ReactNode } from "react";
import { IconRefresh, IconSend } from "@tabler/icons-react";
import moment from "moment/moment";
import styles from "./MailLogCard.module.css";
import Text from "../UI/Text";
import Status from "../UI/Status";
import Avatar from "../UI/Avatar";
import Button from "../UI/Button";
import CollapsibleCard from "../UI/CollapsibleCard";
import DocumentDownloadLink from "../UI/DocumentDownloadLink";
// @ts-expect-error pure js
import DocumentThumbnail from "../UI/DocumentThumbnail";
// @ts-expect-error pure js
import { DocType } from "../../utils/constants/documents";
import type { DownloadableFile } from "../../hooks/use-document-download";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
import {
  MailLogsStatus,
  type MailLog,
  type MailLogAttachment,
} from "../../models/mailLog";

interface MailLogCardProps {
  mail: MailLog;
  onRetry: (id: number) => void;
  onCancel: (id: number) => void;
  onSendNow: (id: number) => void;
  isRetrying: boolean;
  isCancelling: boolean;
  isSending: boolean;
}

interface AttachmentInfo {
  filename: string;
  id: number | null;
  mimetype?: string;
}

function isScheduled(mail: MailLog): boolean {
  return (
    mail.status === MailLogsStatus.PENDING &&
    !!mail.scheduled_at &&
    new Date(mail.scheduled_at) > new Date()
  );
}

function getRecipientDisplay(mail: MailLog): string {
  if (mail.recap?.locataire) return mail.recap.locataire;
  return mail.destinataire;
}

function getInitials(name: string): string {
  const parts = name.split(/[\s.@_-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function getDateForDisplay(mail: MailLog) {
  if (mail.sent_at) return moment(mail.sent_at);
  if (mail.scheduled_at) return moment(mail.scheduled_at);
  return null;
}

function getAttachmentInfo(attachment: MailLogAttachment): AttachmentInfo {
  if (typeof attachment === "string") {
    return { filename: attachment, id: null };
  }
  return {
    filename: attachment.filename || attachment.path || "",
    id: attachment.id ?? null,
    mimetype: "application/pdf",
  };
}

function MailStatusBadge({ mail }: { mail: MailLog }): ReactNode {
  if (mail.status === MailLogsStatus.SENT) {
    return <Status color="success" text="Envoyé" />;
  }
  if (isScheduled(mail)) {
    return <Status color="info" text="Planifié" />;
  }
  if (mail.status === MailLogsStatus.PENDING) {
    return <Status color="violet" text="En cours d'envoi" />;
  }
  if (mail.status === MailLogsStatus.ERROR) {
    return <Status color="danger" text="Erreur" />;
  }
  if (mail.status === MailLogsStatus.CANCELLED) {
    return <Status color="neutral" text="Annulé" />;
  }
  return null;
}

export default function MailLogCard({
  mail,
  onRetry,
  onCancel,
  onSendNow,
  isRetrying,
  isCancelling,
  isSending,
}: MailLogCardProps) {
  const recipientName = getRecipientDisplay(mail);
  const initials = getInitials(recipientName);
  const date = getDateForDisplay(mail);
  const scheduled = isScheduled(mail);
  const attachmentsCount = mail.attachments?.length ?? 0;

  return (
    <CollapsibleCard
      variant="card"
      title={
        <>
          <Avatar size="md" variant="blue" initials={initials} />
          <div className={styles.info}>
            <div className={styles.ellipsis}>
              <Text as="span" weight="bold">
                À&nbsp;:
              </Text>
              &nbsp;
              <Text as="span" weight="medium">
                {recipientName}
              </Text>
            </div>
            <div className={styles.subjectLine}>
              <Text as="span" className={styles.ellipsis}>
                {mail.subject}
              </Text>
              <MailStatusBadge mail={mail} />
            </div>
          </div>
        </>
      }
      headerActions={
        date ? (
          <Text as="div" className={styles.dateBlock}>
            {date.format("DD MMMM YYYY")}
            <br />
            {date.format("HH:mm")}
          </Text>
        ) : undefined
      }
    >
      <div className={styles.panelContent}>
        <div className={styles.panelHeader}>
          <Text weight="bold">{mail.subject}</Text>
          <MailStatusBadge mail={mail} />
        </div>

        <div className={styles.recipientBlock}>
          <div>
            <Text as="span" weight="bold">
              À&nbsp;:&nbsp;
            </Text>
            <Text as="span" className={styles.breakWord}>
              {mail.destinataire}
            </Text>
          </div>
          {mail.cc && (
            <div>
              <Text as="span" weight="bold">
                CC&nbsp;:&nbsp;
              </Text>
              <Text as="span" className={styles.breakWord}>
                {mail.cc}
              </Text>
            </div>
          )}
          {scheduled && (
            <div>
              <Text as="span" variant="deci" weight="bold">
                Planifié&nbsp;:&nbsp;
              </Text>
              <Text as="span" variant="deci" className={styles.breakWord}>
                {moment(mail.scheduled_at).format("DD MMMM YYYY [à] HH:mm")}
              </Text>
            </div>
          )}
        </div>

        {mail.html && (
          <div
            className={styles.breakWord}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(mail.html) }}
          />
        )}

        {attachmentsCount > 0 && (
          <div className={styles.attachments}>
            <Text weight="bold">
              {attachmentsCount}{" "}
              {attachmentsCount > 1 ? "Pièces jointes" : "Pièce jointe"}
            </Text>
            <div className={styles.attachmentGrid}>
              {mail.attachments.map((attachment, index) => {
                const file = getAttachmentInfo(attachment);
                if (file.id === null) {
                  return (
                    <Text key={index} variant="deci">
                      {file.filename}
                    </Text>
                  );
                }
                const downloadableFile: DownloadableFile = {
                  id: file.id,
                  filename: file.filename,
                  mimetype: file.mimetype,
                };
                return (
                  <DocumentDownloadLink
                    key={index}
                    documentId={file.id}
                    file={downloadableFile}
                    doctype={DocType.GENERIC_FILE}
                  >
                    <DocumentThumbnail
                      documentId={file.id}
                      file={downloadableFile}
                      doctype={DocType.GENERIC_FILE}
                    />
                  </DocumentDownloadLink>
                );
              })}
            </div>
          </div>
        )}

        {(scheduled || mail.status === MailLogsStatus.ERROR) && (
          <div className={styles.actions}>
            {scheduled && (
              <>
                <Button
                  variant="secondary"
                  onClick={() => onCancel(mail.id)}
                  isLoading={isCancelling}
                >
                  Annuler l'envoi
                </Button>
                <Button
                  variant="primary"
                  icon={IconSend}
                  onClick={() => onSendNow(mail.id)}
                  isLoading={isSending}
                >
                  Envoyer immédiatement
                </Button>
              </>
            )}
            {mail.status === MailLogsStatus.ERROR && (
              <Button
                variant="primary"
                icon={IconRefresh}
                onClick={() => onRetry(mail.id)}
                isLoading={isRetrying}
              >
                Renvoyer
              </Button>
            )}
          </div>
        )}
      </div>
    </CollapsibleCard>
  );
}
